import * as go from 'gojs';
import { getImageAdd } from 'unitData/Add/getImageAdd';
import { Template } from '../../utils/addAndSplitTemplate';
import theme from 'theme/theme';
import type { AddSplitBaseNodeData, DPorts } from 'gojs/types';

const $ = go.GraphObject.make;

const alignAddPorts = (
  nbrOfInputPorts: number,
  portIndex: number,
  angle: number,
) => {
  if (angle === 90) {
    switch (portIndex) {
      case 0:
        return new go.Spot(go.Spot.Left.x, go.Spot.Left.y, 0, 0);
      case 1:
        return new go.Spot(go.Spot.TopRight.x, go.Spot.TopRight.y, -0.5, 0);
      case 2:
        return new go.Spot(
          go.Spot.BottomRight.x,
          go.Spot.BottomRight.y,
          -0.5,
          0,
        );
      case 3: {
        if (nbrOfInputPorts === 3) {
          return new go.Spot(go.Spot.Right.x, go.Spot.Right.y, -0.5, -0.5);
        } else {
          return new go.Spot(go.Spot.Top.x, go.Spot.Top.y, 0, 0);
        }
      }
      case 4:
        return new go.Spot(go.Spot.Bottom.x, go.Spot.Bottom.y, -0.5, -0.5);
    }
  } else if (angle === 180) {
    switch (portIndex) {
      case 0:
        return new go.Spot(go.Spot.Top.x, go.Spot.Top.y, 0, 0);
      case 1:
        return new go.Spot(
          go.Spot.BottomRight.x,
          go.Spot.BottomRight.y,
          -0.5,
          -0.5,
        );
      case 2:
        return new go.Spot(go.Spot.BottomLeft.x, go.Spot.BottomLeft.y, 0, -0.5);
      case 3: {
        if (nbrOfInputPorts === 3) {
          return new go.Spot(go.Spot.Bottom.x, go.Spot.Bottom.y, 0, -0.5);
        } else {
          return new go.Spot(go.Spot.Right.x, go.Spot.Right.y, -0.5, -0.25);
        }
      }
      case 4:
        return new go.Spot(go.Spot.Left.x, go.Spot.Left.y, 0, -0.23);
    }
  } else if (angle === 270) {
    switch (portIndex) {
      case 0:
        return new go.Spot(go.Spot.Right.x, go.Spot.Right.y, 0, 0);
      case 1:
        return new go.Spot(go.Spot.BottomLeft.x, go.Spot.BottomLeft.y, 0, -0.5);
      case 2:
        return new go.Spot(go.Spot.TopLeft.x, go.Spot.TopLeft.y, 0, 0);
      case 3: {
        if (nbrOfInputPorts === 3) {
          return new go.Spot(go.Spot.Left.x, go.Spot.Left.y, 0, -0.5);
        } else {
          return new go.Spot(go.Spot.Bottom.x, go.Spot.Bottom.y, 0, -0.5);
        }
      }
      case 4:
        return new go.Spot(go.Spot.Top.x, go.Spot.Top.y, 0, 0);
    }
  } else {
    switch (portIndex) {
      case 0:
        return new go.Spot(go.Spot.Bottom.x, go.Spot.Bottom.y, 0, -0.5);
      case 1:
        return new go.Spot(go.Spot.TopLeft.x, go.Spot.TopLeft.y, 0, 0);
      case 2:
        return new go.Spot(go.Spot.TopRight.x, go.Spot.TopRight.y, -0.5, 0);
      case 3: {
        if (nbrOfInputPorts === 3) {
          return new go.Spot(go.Spot.Top.x, go.Spot.Top.y, 0, 0);
        } else {
          return new go.Spot(go.Spot.Left.x, go.Spot.Left.y, 0, -0.5);
        }
      }
      case 4:
        return new go.Spot(go.Spot.Right.x, go.Spot.Right.y, -0.5, 0);
    }
  }
};

const Output = () =>
  $(
    go.Panel,
    'Auto',
    new go.Binding('itemArray', 'D0'),
    new go.Binding('alignment', '', (node) => {
      return alignAddPorts(node.nbrOfInputs, 0, node.angle);
    }),
    {
      itemTemplate: $(
        go.Panel,
        'Spot',
        {
          fromLinkable: true,
          fromMaxLinks: 1,
          cursor: 'pointer',
          alignment: go.Spot.Bottom,
        },
        new go.Binding('portId', 'key'),
        $(
          go.Shape,
          'Triangle',
          {
            fill: theme.palette.info.main,
            stroke: null,
          },
          new go.Binding('angle', '', (node) => {
            return node.part.data.angle + 180;
          }).ofObject(),
          new go.Binding('desiredSize', '', (node) => {
            return node.part.data['D0'][0].connected
              ? new go.Size(6, 6)
              : new go.Size(12, 12);
          }).ofObject(),
        ),
      ),
    },
  );

const Input = (index: number, port: keyof DPorts) => [
  $(
    go.Panel,
    'Auto',
    new go.Binding('itemArray', `D${index}`),
    new go.Binding('alignment', '', (data: AddSplitBaseNodeData) => {
      return alignAddPorts(data.nbrOfInputs, index, data.angle);
    }),
    new go.Binding('visible', 'nbrOfInputs', (nbrOfInputs) => {
      return nbrOfInputs >= index;
    }),
    {
      itemTemplate: $(
        go.Panel,
        'Spot',
        {
          toLinkable: true,
          toMaxLinks: 1,
          toEndSegmentLength: 0,
        },
        new go.Binding('toSpot', '', (data, node) => {
          return node.panel.panel.alignment;
        }),
        new go.Binding('portId', 'key'),
      ),
    },
    $(
      go.Panel,
      'Spot',
      new go.Binding('alignment', '', (data: AddSplitBaseNodeData) => {
        return alignAddPorts(data.nbrOfInputs, index, data.angle);
      }),
      new go.Binding('visible', '', (data: AddSplitBaseNodeData) => {
        return data.nbrOfInputs >= index;
      }),
      $(
        go.Shape,
        'Circle',
        {
          width: 6,
          height: 6,
          fill: theme.palette.info.main,
          stroke: null,
        },
        new go.Binding('opacity', '', (data: AddSplitBaseNodeData) => {
          return data[port]?.[0].connected ? 0 : 1;
        }),
      ),
    ),
  ),
];

const AddTemplate = Template(
  [
    Output(),
    ...Input(1, 'D1'),
    ...Input(2, 'D2'),
    ...Input(3, 'D3'),
    ...Input(4, 'D4'),
  ],
  (node) => getImageAdd(node.nbrOfInputs, node.hasFlow),
);

export default AddTemplate;
